import React, { useState } from 'react';
import { Zap } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast, { Toaster } from 'react-hot-toast';

const CallToAction = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('waitlist')
        .insert([{ email }]);

      if (error) throw error;

      toast.success('Thanks for joining the waitlist! We\'ll be in touch soon.');
      setEmail('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="cta" className="py-24 bg-gradient-to-r from-slate-900 to-slate-800 relative overflow-hidden">
      <Toaster position="top-center" />
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-teal-500 rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-teal-500 to-purple-500 opacity-30"></div>
      </div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-3xl max-w-5xl mx-auto p-8 md:p-12 border border-slate-700 shadow-xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Zap className="h-6 w-6 text-teal-400" />
                <h3 className="text-xl font-semibold">Join the Waitlist</h3>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Be Among the First to Experience Siva</h2>
              
              <p className="text-slate-300 text-lg mb-8">
                Join our exclusive waitlist to get early access to Siva's intelligent assistance.
                Be the first to revolutionize your workflow.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-3 bg-gradient-to-r from-teal-400 to-cyan-500 text-white rounded-lg font-medium hover:shadow-lg hover:from-teal-500 hover:to-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Joining...' : 'Join Waitlist'}
                </button>
                <p className="text-sm text-slate-400 text-center">No credit card required. Be the first to know when we launch.</p>
              </form>
            </div>
            
            <div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-700/50 border border-slate-600 rounded-xl p-4">
                  <div className="text-3xl font-bold text-teal-400 mb-2">+40%</div>
                  <p className="text-slate-300">Productivity increase</p>
                </div>
                <div className="bg-slate-700/50 border border-slate-600 rounded-xl p-4">
                  <div className="text-3xl font-bold text-blue-400 mb-2">10h+</div>
                  <p className="text-slate-300">Weekly time saved</p>
                </div>
                <div className="bg-slate-700/50 border border-slate-600 rounded-xl p-4">
                  <div className="text-3xl font-bold text-purple-400 mb-2">98%</div>
                  <p className="text-slate-300">Customer satisfaction</p>
                </div>
                <div className="bg-slate-700/50 border border-slate-600 rounded-xl p-4">
                  <div className="text-3xl font-bold text-cyan-400 mb-2">24/7</div>
                  <p className="text-slate-300">Available assistance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallToAction;